import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { seedAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, Loader2 } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-check-85/artifacts/kqzoobkc_REVYNIQX_TM-01.png';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Auto-seed database on page load
    const initSeed = async () => {
      try {
        await seedAPI.seed();
      } catch (error) {
        // Already seeded or error - ignore
      }
    };
    initSeed();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDatabase = async () => {
    setSeeding(true);
    try {
      const response = await seedAPI.seed();
      toast.success(response.data.message);
    } catch (error) {
      toast.info('Database already seeded with sample data');
    } finally {
      setSeeding(false);
    }
  };

  const demoCredentials = [
    { role: 'Admin', email: 'admin@revyniqx.com', password: 'admin123' },
    { role: 'Auditor', email: 'auditor@revyniqx.com', password: 'auditor123' },
    { role: 'SPOC', email: 'it.spoc@revyniqx.com', password: 'spoc123' },
    { role: 'Management', email: 'ceo@revyniqx.com', password: 'ceo123' },
  ];

  return (
    <div className="login-container">
      {/* Left Side - Login Form */}
      <div className="login-form-section">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <img src={LOGO_URL} alt="REVYNIQX" className="h-12 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome Back
            </h1>
            <p className="text-slate-600 mt-2">
              Sign in to access the ISO 27001 Audit Platform
            </p>
          </div>

          <Card className="login-form-card border-0 shadow-lg">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#4A5690] hover:bg-[#3D477A] btn-scale"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-xs text-slate-500 mb-3 text-center">Demo Credentials</p>
                <div className="grid grid-cols-2 gap-2">
                  {demoCredentials.map((cred) => (
                    <button
                      key={cred.role}
                      type="button"
                      onClick={() => {
                        setEmail(cred.email);
                        setPassword(cred.password);
                      }}
                      className="text-xs p-2 rounded border border-slate-200 hover:bg-slate-50 transition-colors text-left"
                      data-testid={`demo-${cred.role.toLowerCase()}-btn`}
                    >
                      <span className="font-medium text-[#4A5690]">{cred.role}</span>
                      <br />
                      <span className="text-slate-500">{cred.email}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleSeedDatabase}
                  disabled={seeding}
                  data-testid="seed-database-btn"
                >
                  {seeding ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Seeding...
                    </>
                  ) : (
                    'Initialize Demo Data'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Image & Quote */}
      <div className="login-image-section hidden lg:flex">
        <div className="max-w-lg">
          <Shield size={48} className="mb-6 text-white/80" />
          <blockquote className="text-2xl font-light leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            "Information security is not just about technology; it's about people, processes, and continuous improvement."
          </blockquote>
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/30"></div>
            <span className="text-sm text-white/70">ISO 27001 Compliance</span>
            <div className="h-px flex-1 bg-white/30"></div>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold">93</div>
              <div className="text-sm text-white/70">ISO Controls</div>
            </div>
            <div>
              <div className="text-3xl font-bold">4</div>
              <div className="text-sm text-white/70">Categories</div>
            </div>
            <div>
              <div className="text-3xl font-bold">100%</div>
              <div className="text-sm text-white/70">Digital Audit</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
